import DashboardAnalyticsService from '../../services/dashboardAnalytics.service';
import DashboardPreferenceService from '../../services/dashboardPreference.service';
import { toast } from 'react-toastify';

// Action Types
export const FETCH_KPI_REQUEST = 'FETCH_KPI_REQUEST';
export const FETCH_KPI_SUCCESS = 'FETCH_KPI_SUCCESS';
export const FETCH_KPI_FAILURE = 'FETCH_KPI_FAILURE';

export const FETCH_CHART_REQUEST = 'FETCH_CHART_REQUEST';
export const FETCH_CHART_SUCCESS = 'FETCH_CHART_SUCCESS';
export const FETCH_CHART_FAILURE = 'FETCH_CHART_FAILURE';

export const UPDATE_DASHBOARD_FILTERS = 'UPDATE_DASHBOARD_FILTERS';
export const CLEAR_DASHBOARD_DATA = 'CLEAR_DASHBOARD_DATA';

// New Aggregation Action Types
export const FETCH_AGGREGATED_REQUEST = 'FETCH_AGGREGATED_REQUEST';
export const FETCH_AGGREGATED_SUCCESS = 'FETCH_AGGREGATED_SUCCESS';
export const FETCH_AGGREGATED_FAILURE = 'FETCH_AGGREGATED_FAILURE';

// Company and Seller Dropdown Action Types
export const FETCH_COMPANIES_REQUEST = 'FETCH_COMPANIES_REQUEST';
export const FETCH_COMPANIES_SUCCESS = 'FETCH_COMPANIES_SUCCESS';
export const FETCH_COMPANIES_FAILURE = 'FETCH_COMPANIES_FAILURE';
export const FETCH_SELLERS_REQUEST = 'FETCH_SELLERS_REQUEST';
export const FETCH_SELLERS_SUCCESS = 'FETCH_SELLERS_SUCCESS';
export const FETCH_SELLERS_FAILURE = 'FETCH_SELLERS_FAILURE';

// Dashboard Preference Action Types
export const SAVE_PREFERENCE_REQUEST = 'SAVE_PREFERENCE_REQUEST';
export const SAVE_PREFERENCE_SUCCESS = 'SAVE_PREFERENCE_SUCCESS';
export const SAVE_PREFERENCE_FAILURE = 'SAVE_PREFERENCE_FAILURE';
export const LOAD_PREFERENCE_REQUEST = 'LOAD_PREFERENCE_REQUEST';
export const LOAD_PREFERENCE_SUCCESS = 'LOAD_PREFERENCE_SUCCESS';
export const LOAD_PREFERENCE_FAILURE = 'LOAD_PREFERENCE_FAILURE';
export const FETCH_ALL_PREFERENCES_SUCCESS = 'FETCH_ALL_PREFERENCES_SUCCESS';
export const DELETE_PREFERENCE_SUCCESS = 'DELETE_PREFERENCE_SUCCESS';

const parsePreferencePayload = (filtersJson) => {
    const parsedPreference = JSON.parse(filtersJson);
    const isNewPreferenceShape = parsedPreference && typeof parsedPreference === 'object' && parsedPreference.filters;

    const filters = isNewPreferenceShape ? parsedPreference.filters : parsedPreference;
    const chartTypes = isNewPreferenceShape ? (parsedPreference.chartTypes || {}) : {};
    const visibleWidgets = isNewPreferenceShape ? (parsedPreference.visibleWidgets || {}) : {};
    const widgetOrder = isNewPreferenceShape ? (parsedPreference.widgetOrder || {}) : {};
    const widgetLayout = isNewPreferenceShape ? (parsedPreference.widgetLayout || {}) : {};
    const customTitles = isNewPreferenceShape ? (parsedPreference.customTitles || {}) : {};

    if (filters.fromDate) filters.fromDate = new Date(filters.fromDate);
    if (filters.toDate) filters.toDate = new Date(filters.toDate);

    return { filters, chartTypes, visibleWidgets, widgetOrder, widgetLayout, customTitles };
};

// Action Creators
export const fetchKPISummary = (filters) => async (dispatch) => {
    dispatch({ type: FETCH_KPI_REQUEST });
    try {
        const response = await DashboardAnalyticsService.getKPISummary(filters);
        if (response.data?.Status === 200) {
            // Transform PascalCase from API to camelCase for frontend
            const kpiData = response.data.Entity;
            const normalizedData = {
                totalLabels: kpiData.TotalLabels || kpiData.totalLabels || 0,
                outboundOrders: kpiData.OutboundOrders || kpiData.outboundOrders || 0,
                totalSKUs: kpiData.TotalSKUs || kpiData.totalSKUs || 0,
                inboundConsignments: kpiData.InboundConsignments || kpiData.inboundConsignments || 0,
                openTasks: kpiData.OpenTasks || kpiData.openTasks || 0,
                walletBalance: kpiData.WalletBalance || kpiData.walletBalance || 0,
                labelsChangePercentage: kpiData.LabelsChangePercentage || kpiData.labelsChangePercentage || 0,
                ordersChangePercentage: kpiData.OrdersChangePercentage || kpiData.ordersChangePercentage || 0,
                skUsChangePercentage: kpiData.SKUsChangePercentage || kpiData.skUsChangePercentage || 0,
                inboundChangePercentage: kpiData.InboundChangePercentage || kpiData.inboundChangePercentage || 0,
                tasksChangePercentage: kpiData.TasksChangePercentage || kpiData.tasksChangePercentage || 0
            };
            
            dispatch({
                type: FETCH_KPI_SUCCESS,
                payload: normalizedData
            });
        } else {
            dispatch({
                type: FETCH_KPI_FAILURE,
                payload: response.data?.Message?.AppStatusDescription || 'Failed to fetch KPI data'
            });
            toast.error('Failed to load dashboard KPIs');
        }
    } catch (error) {
        dispatch({
            type: FETCH_KPI_FAILURE,
            payload: error.message
        });
        toast.error('Error loading dashboard data');
    }
};

export const fetchChartData = (chartType, filters) => async (dispatch) => {
    dispatch({ type: FETCH_CHART_REQUEST, chartType });
    try {
        let response;
        switch (chartType) {
            case 'labelsByStatus':
                response = await DashboardAnalyticsService.getLabelsByStatus(filters);
                break;
            case 'orderVolume':
                response = await DashboardAnalyticsService.getOrderVolumeChart(filters);
                break;
            case 'labelsByCarrier':
                response = await DashboardAnalyticsService.getLabelsByCarrier(filters);
                break;
            case 'inboundByStatus':
                response = await DashboardAnalyticsService.getInboundByStatus(filters);
                break;
            case 'tasksByType':
                response = await DashboardAnalyticsService.getTasksByType(filters);
                break;
            default:
                throw new Error('Invalid chart type');
        }

        if (response.data?.Status === 200) {
            // Transform PascalCase from API to camelCase for frontend
            const chartData = response.data.Entity;
            const normalizedData = {
                labels: chartData.Labels || chartData.labels || [],
                datasets: (chartData.Datasets || chartData.datasets || []).map(dataset => ({
                    label: dataset.Label || dataset.label,
                    data: dataset.Data || dataset.data,
                    backgroundColor: dataset.BackgroundColor || dataset.backgroundColor,
                    borderColor: dataset.BorderColor || dataset.borderColor,
                    borderWidth: dataset.BorderWidth || dataset.borderWidth
                }))
            };
            
            dispatch({
                type: FETCH_CHART_SUCCESS,
                chartType,
                payload: normalizedData
            });
        } else {
            dispatch({
                type: FETCH_CHART_FAILURE,
                chartType,
                payload: response.data?.Message?.AppStatusDescription || 'Failed to fetch chart data'
            });
        }
    } catch (error) {
        dispatch({
            type: FETCH_CHART_FAILURE,
            chartType,
            payload: error.message
        });
        toast.error(`Error loading ${chartType} chart`);
    }
};

export const updateDashboardFilters = (filters) => ({
    type: UPDATE_DASHBOARD_FILTERS,
    payload: filters
});

export const clearDashboardData = () => ({
    type: CLEAR_DASHBOARD_DATA
});

/**
 * Fetch all 41 widgets in a single aggregated API call
 * Replaces individual fetchKPISummary + fetchChartData calls
 */
export const fetchAggregatedDashboard = (filters) => async (dispatch) => {
    dispatch({ type: FETCH_AGGREGATED_REQUEST });

    try {
        const response = await DashboardAnalyticsService.getAggregatedDashboard(filters);
        
        if (response.data?.Status === 200) {
            const aggregatedData = response.data.Entity;
            
            // Normalize PascalCase to camelCase for frontend consistency
            const kpiSummary = aggregatedData.KPISummary || {};
            const normalizedData = {
                // KPI Summary - merged from KPISummary block + standalone KPI fields so that
                // role-restricted dashboards (e.g. Warehouse Worker without KPISummary) still
                // surface their phase-specific KPIs.
                kpis: {
                    totalLabels: kpiSummary.TotalLabels || 0,
                    outboundOrders: kpiSummary.OutboundOrders || 0,
                    totalSKUs: kpiSummary.TotalSKUs || 0,
                    inboundConsignments: kpiSummary.InboundConsignments || 0,
                    openTasks: kpiSummary.OpenTasks || 0,
                    walletBalance: kpiSummary.WalletBalance || 0,
                    totalCarrierConnects: aggregatedData.TotalCarrierConnects || 0,
                    pendingPickCount: aggregatedData.PickingPlanStatus || 0,
                    pendingPackCount: aggregatedData.PackingPlanStatus || 0,
                    inboundsPendingCount: aggregatedData.InboundsPendingCount || 0,
                    returnsPendingCount: aggregatedData.ReturnsPendingCount || 0,
                    labelsChangePercentage: kpiSummary.LabelsChangePercentage || 0,
                    ordersChangePercentage: kpiSummary.OrdersChangePercentage || 0,
                    skUsChangePercentage: kpiSummary.SKUsChangePercentage || 0,
                    inboundChangePercentage: kpiSummary.InboundChangePercentage || 0,
                    tasksChangePercentage: kpiSummary.TasksChangePercentage || 0
                },
                
                // All 41 widgets (normalized from PascalCase)
                widgets: {
                    // Existing 6 widgets
                    dailyShipments: normalizeChartData(aggregatedData.DailyShipments),
                    dailyOrders: normalizeChartData(aggregatedData.DailyOrders),
                    carrierDistribution: normalizeChartData(aggregatedData.CarrierDistribution),
                    activeShipmentsStatus: normalizeChartData(aggregatedData.ActiveShipmentsStatus),
                    tasksByType: normalizeChartData(aggregatedData.TasksByType),
                    
                    // Phase 1 (9 remaining from original 15)
                    monthlyRevenueTrend: normalizeChartData(aggregatedData.MonthlyRevenueTrend),
                    // "Spend Trend" widget consumes the same backend payload as
                    // "Shipping Cost Trend" (SP_Dashboard_GetShippingCostTrend).
                    spendTrend: normalizeChartData(aggregatedData.MonthlyRevenueTrend),
                    shippingServiceBreakdown: normalizeChartData(aggregatedData.ShippingServiceBreakdown),
                    orderStatusDistribution: normalizeChartData(aggregatedData.OrderStatusDistribution),
                    revenueByMarketplace: normalizeChartData(aggregatedData.RevenueByMarketplace),
                    dailyConsignments: normalizeChartData(aggregatedData.DailyConsignments),
                    inboundByConsignmentType: normalizeChartData(aggregatedData.InboundByConsignmentType),
                    tasksByPriority: normalizeChartData(aggregatedData.TasksByPriority),
                    carrierConnectsByCarrier: normalizeChartData(aggregatedData.CarrierConnectsByCarrier),
                    carrierConnectsByBillingStatus: normalizeChartData(aggregatedData.CarrierConnectsByBillingStatus),
                    
                    // Phase 2 - Shipment Performance (4)
                    averageShippingCost: aggregatedData.AverageShippingCost,
                    shipmentsByWeightClass: normalizeChartData(aggregatedData.ShipmentsByWeightClass),
                    topShippingDestinations: normalizeChartData(aggregatedData.TopShippingDestinations),
                    carrierPerformanceScore: normalizeChartData(aggregatedData.CarrierPerformanceScore),
                    
                    // Phase 2 - Order Analytics (4)
                    hourlyShipments: normalizeChartData(aggregatedData.HourlyShipments),
                    averageOrderValue: aggregatedData.AverageOrderValue,
                    ordersByProductCategory: normalizeChartData(aggregatedData.OrdersByProductCategory),
                    orderCancellationRate: aggregatedData.OrderCancellationRate,
                    
                    // Phase 2 - Inventory Insights (4)
                    topProducts: normalizeChartData(aggregatedData.TopProducts),
                    productDiversity: aggregatedData.ProductDiversity,
                    lowStockCount: aggregatedData.LowStockCount,
                    inventoryValueByCategory: normalizeChartData(aggregatedData.InventoryValueByCategory),
                    weeklyOrderComparison: normalizeChartData(aggregatedData.WeeklyOrderComparison),
                    
                    // Phase 2 - Operational Efficiency (4)
                    taskCompletionRate: aggregatedData.TaskCompletionRate,
                    averageTaskDuration: aggregatedData.AverageTaskDuration,
                    consignmentProcessingTime: aggregatedData.ConsignmentProcessingTime,
                    peakShippingHours: normalizeChartData(aggregatedData.PeakShippingHours),
                    
                    // Phase 3 - Advanced Analytics (10)
                    shippingCostVarianceAnalysis: normalizeChartData(aggregatedData.ShippingCostVarianceAnalysis),
                    carrierReliabilityScore: normalizeChartData(aggregatedData.CarrierReliabilityScore),
                    shipmentRouteEfficiencyMatrix: normalizeChartData(aggregatedData.ShipmentRouteEfficiencyMatrix),
                    orderFulfillmentCycleTime: aggregatedData.OrderFulfillmentCycleTime,
                    customerRepeatOrderRate: aggregatedData.CustomerRepeatOrderRate,
                    orderValueDistribution: aggregatedData.OrderValueDistribution,
                    skuProfitabilityIndex: normalizeChartData(aggregatedData.SKUProfitabilityIndex),
                    inventoryTurnoverVelocity: aggregatedData.InventoryTurnoverVelocity,
                    multiChannelPerformanceScorecard: normalizeChartData(aggregatedData.MultiChannelPerformanceScorecard),
                    peakHourCapacityUtilization: normalizeChartData(aggregatedData.PeakHourCapacityUtilization),

                    // Returns & Refunds (7)
                    returnPackagesCount: aggregatedData.ReturnPackagesCount,
                    returnPackagesByType: normalizeChartData(aggregatedData.ReturnPackagesByType),
                    returnProductsByStatus: normalizeChartData(aggregatedData.ReturnProductsByStatus),
                    returnBillingStatus: normalizeChartData(aggregatedData.ReturnBillingStatus),
                    refundRequestStatus: normalizeChartData(aggregatedData.RefundRequestStatus),
                    returnsPendingCount: aggregatedData.ReturnsPendingCount,
                    refundVolumeByCompany: normalizeChartData(aggregatedData.RefundVolumeByCompany),

                    // Operations & Tasks (Phase 2 - Warehouse Worker)
                    outboundQueueByCompany: normalizeChartData(aggregatedData.OutboundQueueByCompany)
                },
                
                // Metadata
                metadata: {
                    totalWidgets: aggregatedData.TotalWidgets || 41,
                    successfulWidgets: aggregatedData.SuccessfulWidgets || 0,
                    failedWidgets: aggregatedData.FailedWidgets || 0,
                    executionTimeMs: aggregatedData.ExecutionTimeMs || 0,
                    generatedAtUtc: aggregatedData.GeneratedAtUtc || 0,
                    errors: aggregatedData.Errors || {}
                }
            };

            dispatch({
                type: FETCH_AGGREGATED_SUCCESS,
                payload: normalizedData
            });

            return normalizedData;
        }

        throw new Error(response.data?.Message?.AppStatusDescription || 'Failed to fetch dashboard data');
    } catch (error) {
        dispatch({
            type: FETCH_AGGREGATED_FAILURE,
            payload: error.message
        });
        toast.error('Error loading aggregated dashboard');
        return null;
    }
};

/**
 * Helper function to normalize chart data from API format
 */
const normalizeChartData = (chartData) => {
    if (!chartData) return null;
    
    return {
        labels: chartData.Labels || chartData.labels || [],
        datasets: (chartData.Datasets || chartData.datasets || []).map(dataset => ({
            label: dataset.Label || dataset.label,
            data: dataset.Data || dataset.data,
            backgroundColor: dataset.BackgroundColor || dataset.backgroundColor,
            borderColor: dataset.BorderColor || dataset.borderColor,
            borderWidth: dataset.BorderWidth || dataset.borderWidth
        }))
    };
};

export const refreshDashboard = (filters) => async (dispatch) => {
    // New approach: Single aggregated call replaces 6+ individual calls
    dispatch(fetchAggregatedDashboard(filters));
};

/**
 * Fetch all companies for dropdown filter
 * Called on dashboard mount
 */
export const fetchCompanies = () => async (dispatch) => {
    dispatch({ type: FETCH_COMPANIES_REQUEST });
    try {
        const response = await DashboardAnalyticsService.getCompanies();
        // Extract companies regardless of strict Status check
        const entity = response.data?.Entity;
        if (entity?.listCompanyMasterOrdersEntity) {
            dispatch({
                type: FETCH_COMPANIES_SUCCESS,
                payload: entity.listCompanyMasterOrdersEntity
            });
            return entity.listCompanyMasterOrdersEntity;
        } else if (response.data?.Status === 200 || response.status === 200) {
            dispatch({
                type: FETCH_COMPANIES_SUCCESS,
                payload: []
            });
            return [];
        } else {
            console.error('[Dashboard] fetchCompanies: unexpected response', response.data);
            dispatch({
                type: FETCH_COMPANIES_FAILURE,
                payload: 'Failed to fetch companies'
            });
            return null;
        }
    } catch (error) {
        console.error('[Dashboard] fetchCompanies error:', error.response?.status, error.message);
        dispatch({
            type: FETCH_COMPANIES_FAILURE,
            payload: error.message
        });
        return null;
    }
};

/**
 * Fetch sellers for selected company IDs
 * Called when user selects a company
 */
export const fetchSellersByCompanyIds = (companyIds) => async (dispatch) => {
    const normalizedCompanyIds = Array.isArray(companyIds)
        ? [...companyIds].map(Number).filter(id => !Number.isNaN(id)).sort((a, b) => a - b)
        : [];

    dispatch({ type: FETCH_SELLERS_REQUEST });
    try {
        const response = await DashboardAnalyticsService.getSellersByCompanyIds(normalizedCompanyIds);
        const entity = response.data?.Entity;
        if (entity?.listSellerMasterOrdersEntity) {
            dispatch({
                type: FETCH_SELLERS_SUCCESS,
                payload: entity.listSellerMasterOrdersEntity
            });
            return entity.listSellerMasterOrdersEntity;
        } else if (response.data && (response.data.Status === 200 || response.status === 200)) {
            dispatch({
                type: FETCH_SELLERS_SUCCESS,
                payload: []
            });
            return [];
        } else {
            console.error('[Dashboard] fetchSellersByCompanyIds: unexpected response', response.data);
            dispatch({
                type: FETCH_SELLERS_FAILURE,
                payload: 'Failed to fetch sellers'
            });
            return null;
        }
    } catch (error) {
        console.error('[Dashboard] fetchSellersByCompanyIds error:', error.response?.status, error.message);
        dispatch({
            type: FETCH_SELLERS_FAILURE,
            payload: error.message
        });
        return null;
    }
};

// ─── Dashboard Preference Actions ────────────────────────────────────────────

/**
 * Save current filters and chart types as a dashboard preference.
 * @param {Object} filters - current dashboard filter state
 * @param {string} name    - preset name (default: 'Default')
 * @param {boolean} isDefault - whether this should be the auto-loaded preset
 * @param {Object} chartTypes - selected chart type per widget
 * @param {Object} visibleWidgets - visible/hidden widget map
 */
export const saveDashboardPreference = (
    filters,
    name = 'Default',
    isDefault = true,
    chartTypes = {},
    visibleWidgets = {},
    widgetOrder = {},
    options = {}
) => async (dispatch) => {
    const { widgetLayout = {}, customTitles = {}, showSuccessToast = true } = options || {};
    dispatch({ type: SAVE_PREFERENCE_REQUEST });
    try {
        const serializedPreference = {
            filters,
            chartTypes,
            visibleWidgets,
            widgetOrder,
            widgetLayout,
            customTitles
        };
        const payload = {
            Name: name,
            FiltersJson: JSON.stringify(serializedPreference),
            IsDefault: isDefault
        };
        const response = await DashboardPreferenceService.save(payload);
        if (response.data?.Status === 200 || response.status === 200) {
            const saved = response.data.Entity || { name, filtersJson: payload.FiltersJson, isDefault };
            dispatch({ type: SAVE_PREFERENCE_SUCCESS, payload: saved });
            if (showSuccessToast) {
                toast.success(`Dashboard preference "${name}" saved successfully!`);
            }
        } else {
            dispatch({ type: SAVE_PREFERENCE_FAILURE, payload: 'Failed to save preference' });
            toast.error('Failed to save dashboard preference.');
        }
    } catch (error) {
        console.error('[Dashboard] saveDashboardPreference error:', error.message);
        dispatch({ type: SAVE_PREFERENCE_FAILURE, payload: error.message });
        toast.error('Failed to save dashboard preference.');
    }
};

/**
 * Load the default saved dashboard preference and apply its filters.
 * Dispatches refreshDashboard with the loaded filters on success.
 */
export const loadDefaultDashboardPreference = () => async (dispatch) => {
    dispatch({ type: LOAD_PREFERENCE_REQUEST });
    try {
        const response = await DashboardPreferenceService.getDefault();
        if (response.data?.Status === 200 || response.status === 200) {
            const entity = response.data.Entity;
            if (entity?.FiltersJson) {
                const { filters, chartTypes, visibleWidgets, widgetOrder, widgetLayout, customTitles } = parsePreferencePayload(entity.FiltersJson);
                dispatch({ type: LOAD_PREFERENCE_SUCCESS, payload: { preference: entity, filters } });
                dispatch(refreshDashboard(filters));
                return { filters, chartTypes, visibleWidgets, widgetOrder, widgetLayout, customTitles };
            }
        }
        // No saved preference — dispatch failure silently (not an error)
        dispatch({ type: LOAD_PREFERENCE_FAILURE, payload: null });
    } catch (error) {
        // 404 means no preference saved yet — not a real error
        if (error.response?.status !== 404) {
            console.error('[Dashboard] loadDefaultDashboardPreference error:', error.message);
        }
        dispatch({ type: LOAD_PREFERENCE_FAILURE, payload: null });
    }
    return null;
};

export const applyDashboardPreference = (preference) => async (dispatch) => {
    if (!preference?.FiltersJson) return null;

    try {
        const { filters, chartTypes, visibleWidgets, widgetOrder, widgetLayout, customTitles } = parsePreferencePayload(preference.FiltersJson);
        dispatch({ type: LOAD_PREFERENCE_SUCCESS, payload: { preference, filters } });
        dispatch(refreshDashboard(filters));
        return { filters, chartTypes, visibleWidgets, widgetOrder, widgetLayout, customTitles };
    } catch (error) {
        console.error('[Dashboard] applyDashboardPreference error:', error.message);
        return null;
    }
};

/**
 * Fetch all saved dashboard presets for the user.
 */
export const fetchAllDashboardPreferences = () => async (dispatch) => {
    try {
        const response = await DashboardPreferenceService.getAll();
        if (response.data?.Status === 200 || response.status === 200) {
            dispatch({
                type: FETCH_ALL_PREFERENCES_SUCCESS,
                payload: response.data.EntityList || response.data.Entity || []
            });
        }
    } catch (error) {
        console.error('[Dashboard] fetchAllDashboardPreferences error:', error.message);
    }
};

/**
 * Delete a saved dashboard preference by ID.
 * @param {number} id
 */
export const deleteDashboardPreference = (id) => async (dispatch) => {
    try {
        await DashboardPreferenceService.remove(id);
        dispatch({ type: DELETE_PREFERENCE_SUCCESS, payload: id });
        toast.success('Dashboard preference deleted.');
    } catch (error) {
        console.error('[Dashboard] deleteDashboardPreference error:', error.message);
        toast.error('Failed to delete preference.');
    }
};
